import React, { FC } from "react";
import {
  CalendarDays,
  CheckCircle2,
  Clock,
  MapPin,
  Plus,
  Timer,
  AlertCircle,
} from "lucide-react";
import LoadingDots from "../../atoms/Utlis/LoadinDots";
import { EnteredSubEventType } from "../../../Data/event.dto";

interface ReviewEventDetailsProps {
  customerName: string;
  customerPhoneNumber: string;
  eventName: string;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  subEvents: EnteredSubEventType[];
  setCurrentSubEvent: React.Dispatch<
    React.SetStateAction<EnteredSubEventType | null>
  >;
  handleRemoveSubEvents: (event: EnteredSubEventType) => void;
  handleSubmit: () => void;
  loading: boolean;
  isFormSubmitted: boolean;
  errorMessage: string;
  showError: boolean;
  validCustomerName: (n: string) => boolean;
  validPhoneNumber: (num: string) => boolean;
  validEventName: (n: string) => boolean;
  validSubEvents: () => boolean;
  setCustomerName: React.Dispatch<React.SetStateAction<string>>;
  setCustomerPhoneNumber: React.Dispatch<React.SetStateAction<string>>;
  setEventName: React.Dispatch<React.SetStateAction<string>>;
  handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const fieldClass =
  "w-full h-[52px] px-4 rounded-2xl bg-white border border-black/10 text-[#171717] font-medium placeholder:text-black/35 focus:outline-none focus:border-[#CE56B0]/50 focus:ring-4 focus:ring-[#CE56B0]/10 transition";
const fieldWrapClass = "flex flex-col gap-2 min-w-0";
const labelClass = "text-xs font-bold uppercase tracking-wide text-black/45 leading-none";

const ReviewEventDetails: FC<ReviewEventDetailsProps> = ({
  customerName,
  customerPhoneNumber,
  eventName,
  setStep,
  subEvents,
  setCurrentSubEvent,
  handleRemoveSubEvents,
  handleSubmit,
  loading,
  isFormSubmitted,
  errorMessage,
  showError,
  validCustomerName,
  validPhoneNumber,
  validEventName,
  validSubEvents,
  setCustomerName,
  setEventName,
  handleChange,
}) => {
  if (isFormSubmitted) {
    return (
      <div className="rounded-[24px] border border-black/[0.06] bg-white/90 px-6 py-14 text-center shadow-[0_20px_50px_rgba(23,23,23,0.06)] sm:px-10">
        {errorMessage ? (
          <>
            <span className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-red-50 text-red-500">
              <AlertCircle size={28} />
            </span>
            <h2 className="font-[Urbanist,sans-serif] text-2xl font-extrabold text-[#171717]">
              Something went wrong
            </h2>
            <p className="mt-2 text-base font-semibold text-red-500">{errorMessage}</p>
            <p className="mt-2 text-sm font-medium text-black/50">
              Please contact your studio for a new link.
            </p>
          </>
        ) : (
          <>
            <span className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl background-linear text-white shadow-lg shadow-[#CE56B0]/25">
              <CheckCircle2 size={28} />
            </span>
            <h2 className="font-[Urbanist,sans-serif] text-2xl font-extrabold text-[#171717]">
              Thank you!
            </h2>
            <p className="mx-auto mt-2 max-w-md text-sm font-medium leading-relaxed text-black/55">
              Your event inquiry has been sent to the studio. They will get in touch with you soon.
            </p>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {showError && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-center">
          <p className="text-sm font-semibold text-red-600">
            Please fix the highlighted fields before submitting.
          </p>
        </div>
      )}

      {/* Customer + event summary */}
      <div className="rounded-[24px] border border-black/[0.06] bg-white/90 p-5 shadow-[0_20px_50px_rgba(23,23,23,0.06)] sm:p-8">
        <div className="mb-5 flex items-center justify-between gap-3">
          <div>
            <h2 className="font-[Urbanist,sans-serif] text-lg font-bold text-[#171717]">
              Your details
            </h2>
            <p className="text-sm font-medium text-black/45">
              Edit anything that needs a quick fix.
            </p>
          </div>
          <button
            type="button"
            className="rounded-xl border border-black/10 bg-white px-3.5 py-1.5 text-sm font-semibold text-black/55 transition hover:border-[#CE56B0]/30 hover:text-[#C94DAA]"
            onClick={() => setStep(1)}
          >
            Edit
          </button>
        </div>

        <div className="grid grid-cols-1 items-start gap-5 md:grid-cols-2">
          <div className={fieldWrapClass}>
            <label className={labelClass}>Your name</label>
            <input
              type="text"
              placeholder="Enter your name"
              className={`${fieldClass} ${
                showError && !validCustomerName(customerName)
                  ? "border-red-400 ring-4 ring-red-100"
                  : ""
              }`}
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
            />
            {showError && !validCustomerName(customerName) && (
              <p className="text-sm text-red-500">Please enter a valid name.</p>
            )}
          </div>
          <div className={fieldWrapClass}>
            <label className={labelClass}>Phone number</label>
            <div className="relative">
              <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-sm font-semibold text-black/40">
                +91
              </span>
              <input
                type="tel"
                inputMode="numeric"
                placeholder="10-digit mobile number"
                className={`${fieldClass} pl-12 ${
                  showError && !validPhoneNumber(customerPhoneNumber)
                    ? "border-red-400 ring-4 ring-red-100"
                    : ""
                }`}
                value={customerPhoneNumber}
                onChange={handleChange}
              />
            </div>
            {showError && !validPhoneNumber(customerPhoneNumber) && (
              <p className="text-sm text-red-500">
                Enter a valid 10-digit number.
              </p>
            )}
          </div>
          <div className={`md:col-span-2 md:max-w-md ${fieldWrapClass}`}>
            <label className={labelClass}>Event name</label>
            <input
              type="text"
              placeholder="Enter event name"
              className={`${fieldClass} ${
                showError && !validEventName(eventName)
                  ? "border-red-400 ring-4 ring-red-100"
                  : ""
              }`}
              value={eventName}
              onChange={(e) => setEventName(e.target.value)}
            />
            {showError && !validEventName(eventName) && (
              <p className="text-sm text-red-500">
                Please enter a valid event name.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Day schedules */}
      <div className="rounded-[24px] border border-black/[0.06] bg-white/90 p-5 shadow-[0_20px_50px_rgba(23,23,23,0.06)] sm:p-8">
        <div className="mb-5 flex items-center justify-between gap-3">
          <div>
            <h2 className="font-[Urbanist,sans-serif] text-lg font-bold text-[#171717]">
              Day schedules{" "}
              <span className="font-semibold text-black/40">({subEvents.length})</span>
            </h2>
            <p className="text-sm font-medium text-black/45">
              Confirm each shoot day looks correct.
            </p>
          </div>
          {subEvents.length < 10 && (
            <button
              type="button"
              className="inline-flex shrink-0 items-center gap-1.5 rounded-xl background-linear px-3.5 py-2 text-sm font-bold text-white shadow-md shadow-[#CE56B0]/20"
              onClick={() => {
                setCurrentSubEvent(null);
                setStep(2);
              }}
            >
              <Plus size={16} />
              Add day
            </button>
          )}
        </div>

        {showError && !validSubEvents() && (
          <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3">
            <p className="text-sm text-red-500">Please add at least one day schedule.</p>
          </div>
        )}

        <div className="flex flex-col gap-4">
          {subEvents.map((event, index) => (
            <div
              key={event.id}
              className="rounded-2xl border border-black/6 bg-[#FAFAFA] p-4 sm:p-5"
            >
              <div className="mb-4 flex items-center justify-between gap-3">
                <span className="rounded-full bg-[#CE56B0]/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#C94DAA]">
                  Day {index + 1}
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="rounded-lg border border-black/10 bg-white px-3 py-1 text-xs font-semibold text-black/55 transition hover:border-[#CE56B0]/30 hover:text-[#C94DAA]"
                    onClick={() => {
                      setCurrentSubEvent(event);
                      setStep(2);
                    }}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="rounded-lg border border-black/10 bg-white px-3 py-1 text-xs font-semibold text-black/55 transition hover:border-red-200 hover:text-red-500"
                    onClick={() => handleRemoveSubEvents(event)}
                  >
                    Remove
                  </button>
                </div>
              </div>

              <h3 className="font-[Urbanist,sans-serif] text-base font-bold text-[#171717]">
                {event.sub_event_name}
              </h3>

              <div className="mt-3 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                <div className="inline-flex items-center gap-2 rounded-xl bg-white px-3 py-2.5 text-sm font-medium text-black/65">
                  <CalendarDays size={14} className="text-[#C94DAA]" />
                  {event.starting_date}
                </div>
                <div className="inline-flex items-center gap-2 rounded-xl bg-white px-3 py-2.5 text-sm font-medium text-black/65">
                  <Clock size={14} className="text-[#C94DAA]" />
                  {String(event.starting_time).slice(0, 5)}
                </div>
                <div className="inline-flex items-center gap-2 rounded-xl bg-white px-3 py-2.5 text-sm font-medium text-black/65">
                  <Timer size={14} className="text-[#C94DAA]" />
                  {event.duration} hour{event.duration === 1 ? "" : "s"}
                </div>
                <div className="inline-flex items-center gap-2 rounded-xl bg-white px-3 py-2.5 text-sm font-medium text-black/65">
                  <MapPin size={14} className="shrink-0 text-[#C94DAA]" />
                  <span className="truncate">{event.sub_event_location}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-center pb-2">
        {loading ? (
          <LoadingDots />
        ) : (
          <button
            type="button"
            className="w-full max-w-xs rounded-2xl background-linear px-8 py-3.5 text-base font-bold text-white shadow-lg shadow-[#CE56B0]/25 transition hover:-translate-y-0.5 sm:w-auto"
            onClick={handleSubmit}
          >
            Submit inquiry
          </button>
        )}
      </div>
    </div>
  );
};

export default ReviewEventDetails;
